import React, { useEffect, useState } from 'react';
import {
  signIn,
  signUp,
  signInWithProvider,
  sendPasswordReset,
  supabaseConfigured,
} from '../lib/supabase';

/* ────────────────────────────────────────────────────────────────────────────
   AuthModal — mini-spec #15

   Cleaned-up single sign-in / sign-up surface. Two paths only:
     1. Continue with Google (OAuth)
     2. Email + password (with show-password toggle and Forgot password)

   The cluster of secondary providers (Apple, Discord, Facebook, Microsoft,
   Phone) was removed — they were either soft-disabled or unconfigured and
   added decision fatigue to the auth step.

   Cinematic theme: glass-panel surface over <AmbientBackground />, glow
   buttons, Inter typography. Matches the new system across the rest of
   the app.

   States: 'auth' (default) | 'forgot' (password-reset email) | 'sent'
   (post-reset confirmation).
   ──────────────────────────────────────────────────────────────────────── */

const LAST_PROVIDER_KEY = 'episode_last_auth_provider';
function getLastProvider() {
  try { return localStorage.getItem(LAST_PROVIDER_KEY) || ''; } catch { return ''; }
}
function setLastProvider(p) {
  try { localStorage.setItem(LAST_PROVIDER_KEY, p); } catch {}
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.6 2.5 30.2 0 24 0 14.8 0 6.9 5.3 3 13l7.8 6.1C12.6 13.3 17.8 9.5 24 9.5z" />
      <path fill="#34A853" d="M46.8 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.8c-.6 3.1-2.4 5.7-5.1 7.5l7.8 6.1c4.6-4.2 7.3-10.4 7.3-17.9z" />
      <path fill="#FBBC05" d="M10.8 28.1A14.5 14.5 0 0 1 10 24c0-1.4.2-2.8.7-4.1L3 13.9A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l8.2-6.6z" />
      <path fill="#4285F4" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.8-6.1c-2.2 1.5-5 2.4-8.1 2.4-6.2 0-11.4-3.8-13.3-9.1l-7.9 6.1A24 24 0 0 0 24 48z" />
    </svg>
  );
}
function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.79 19.79 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.79 19.79 0 0 1-3.17 4.13"/>
      <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export default function AuthModal({ mode, onClose, onSuccess }) {
  const [authMode, setAuthMode] = useState(mode === 'signin' ? 'signin' : 'signup');
  const isSignup = authMode === 'signup';

  const [stage, setStage]       = useState('auth'); // 'auth' | 'forgot' | 'sent'
  const [busy, setBusy]         = useState('');
  const [error, setError]       = useState('');
  const [info,  setInfo]        = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const last = getLastProvider();

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function clearMessages() { setError(''); setInfo(''); }

  async function runOAuth(provider) {
    clearMessages();
    if (!supabaseConfigured) { setError('Auth not configured.'); return; }
    try {
      setBusy(provider);
      setLastProvider(provider);
      await signInWithProvider(provider);
    } catch (e) {
      setBusy('');
      setError(e?.message || 'Sign in failed.');
    }
  }

  async function submitEmail(e) {
    e.preventDefault();
    clearMessages();
    try {
      setBusy('email');
      if (isSignup) await signUp(email, password);
      else          await signIn(email, password);
      setLastProvider('email');
      onSuccess?.();
    } catch (err) {
      setError(err?.message || (isSignup ? 'Sign up failed.' : 'Sign in failed.'));
    } finally {
      setBusy('');
    }
  }

  async function submitForgot(e) {
    e.preventDefault();
    clearMessages();
    try {
      setBusy('forgot');
      await sendPasswordReset(forgotEmail);
      setStage('sent');
    } catch (err) {
      setError(err?.message || 'Could not send reset email.');
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="auth-wrap" role="dialog" aria-modal="true">
      <div className="auth-backdrop" onClick={onClose} />
      <div className="auth-card glass-panel glass-panel--lg">
        <button className="auth-close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {stage === 'auth' && (
          <>
            <div className="auth-head">
              <div className="auth-title">
                {isSignup ? 'Create your account' : 'Welcome back'}
              </div>
              <div className="auth-sub">
                {isSignup
                  ? 'Free to start. No credit card required.'
                  : 'Pick up where you left off.'}
              </div>
            </div>

            <button
              className={`auth-google${busy === 'google' ? ' loading' : ''}${last === 'google' ? ' last' : ''}`}
              onClick={() => runOAuth('google')}
              disabled={busy === 'google'}
              type="button"
            >
              <span className="auth-google-icon"><GoogleIcon /></span>
              <span className="auth-google-label">
                {busy === 'google' ? 'Connecting…' : `Continue with Google`}
              </span>
              {last === 'google' && <span className="auth-last-tag">Last used</span>}
            </button>

            <div className="auth-divider"><span>or</span></div>

            <form className="auth-form" onSubmit={submitEmail}>
              <label className="auth-field">
                <span className="auth-field-label">Email</span>
                <input
                  type="email"
                  required
                  placeholder="you@domain.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  className="auth-input"
                />
              </label>

              <label className="auth-field">
                <span className="auth-field-label-row">
                  <span className="auth-field-label">Password</span>
                  {!isSignup && (
                    <button
                      type="button"
                      className="auth-forgot-link"
                      onClick={() => { clearMessages(); setForgotEmail(email); setStage('forgot'); }}
                    >
                      Forgot password?
                    </button>
                  )}
                </span>
                <span className="auth-input-with-icon">
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    placeholder={isSignup ? 'At least 8 characters' : 'Your password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                    minLength={isSignup ? 8 : undefined}
                    className="auth-input"
                  />
                  <button
                    type="button"
                    className="auth-input-eye"
                    onClick={() => setShowPw(v => !v)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    title={showPw ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showPw} />
                  </button>
                </span>
              </label>

              <button
                type="submit"
                className="btn-glow-primary auth-submit"
                disabled={busy === 'email'}
              >
                {busy === 'email'
                  ? 'Working…'
                  : (isSignup ? 'Create account' : 'Sign in')}
              </button>
            </form>

            {error && <div className="auth-error">{error}</div>}
            {info  && <div className="auth-info">{info}</div>}

            <div className="auth-switch">
              {isSignup ? (
                <>Already have an account?{' '}
                  <button type="button" className="auth-switch-link" onClick={() => { clearMessages(); setAuthMode('signin'); }}>
                    Sign in
                  </button>
                </>
              ) : (
                <>New here?{' '}
                  <button type="button" className="auth-switch-link" onClick={() => { clearMessages(); setAuthMode('signup'); }}>
                    Create an account
                  </button>
                </>
              )}
            </div>

            <div className="auth-legal">
              By continuing, you accept our{' '}
              <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>{' '}and{' '}
              <a href="/terms.html"   target="_blank" rel="noreferrer">Terms of Use</a>.
            </div>
          </>
        )}

        {stage === 'forgot' && (
          <>
            <div className="auth-head">
              <div className="auth-title">Reset your password</div>
              <div className="auth-sub">
                Enter the email on your account — we'll send a recovery link.
              </div>
            </div>

            <form className="auth-form" onSubmit={submitForgot}>
              <label className="auth-field">
                <span className="auth-field-label">Email</span>
                <input
                  type="email"
                  required
                  placeholder="you@domain.com"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  className="auth-input"
                  autoFocus
                />
              </label>

              <button
                type="submit"
                className="btn-glow-primary auth-submit"
                disabled={busy === 'forgot'}
              >
                {busy === 'forgot' ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            {error && <div className="auth-error">{error}</div>}

            <button
              type="button"
              className="auth-back-link"
              onClick={() => { clearMessages(); setStage('auth'); }}
            >
              ← Back to sign in
            </button>
          </>
        )}

        {stage === 'sent' && (
          <>
            <div className="auth-head">
              <div className="auth-title">Check your inbox</div>
              <div className="auth-sub">
                We sent a password-reset link to <strong>{forgotEmail}</strong>.
                It expires in one hour.
              </div>
            </div>
            <button
              type="button"
              className="btn-glow-secondary auth-submit"
              onClick={() => { clearMessages(); setStage('auth'); }}
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
